import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Sparkles, Loader2, ChevronDown, ChevronRight, Check, Copy, ArrowRight } from 'lucide-react';
import RichTextEditor from './RichTextEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERSONAS = [
    { value: 'default', label: 'Default — Balanced & Strategic', icon: '🎯' },
    { value: 'technical', label: 'Technical Writer — Precise & Detailed', icon: '⚙️' },
    { value: 'executive', label: 'Executive Brief — Outcome-Focused', icon: '📊' },
    { value: 'casual', label: 'Casual — Friendly & Approachable', icon: '💬' },
];

const LABEL = "block text-[11px] font-medium text-stone-500 mb-1 uppercase tracking-wide";

function CollapsibleSection({ title, children, defaultOpen = false }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="border border-stone-200 rounded-lg overflow-hidden">
            <button type="button" onClick={() => setOpen(!open)}
                className="w-full flex items-center gap-2 px-3 py-2 bg-stone-50 hover:bg-stone-100 text-left text-xs font-medium text-stone-700">
                {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                {title}
            </button>
            {open && <div className="p-3 text-sm text-stone-700">{children}</div>}
        </div>
    );
}

function PillList({ items = [], color = 'stone' }) {
    return (
        <div className="flex flex-wrap gap-1.5">
            {items.map((item, i) => (
                <span key={i} className={`inline-block px-2.5 py-1 text-xs rounded-full bg-${color}-100 text-${color}-700`}>
                    {typeof item === 'string' ? item : item.title || JSON.stringify(item)}
                </span>
            ))}
        </div>
    );
}

function PersonaResult({ result, persona }) {
    if (!result) return null;
    const copyToClipboard = (text) => { navigator.clipboard.writeText(text); toast.success('Copied'); };

    return (
        <div className="space-y-3">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <p className={LABEL}>Value Proposition</p>
                <p className="text-base font-medium text-stone-900">{result.value_proposition}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <CollapsibleSection title="What It Is" defaultOpen><p>{result.what_it_is}</p></CollapsibleSection>
                <CollapsibleSection title="Who It's For" defaultOpen><p>{result.who_it_is_for}</p></CollapsibleSection>
                <CollapsibleSection title="The Problem It Solves" defaultOpen><p>{result.the_problem}</p></CollapsibleSection>
                <CollapsibleSection title="What It Does" defaultOpen><p>{result.what_it_does}</p></CollapsibleSection>
            </div>
            <CollapsibleSection title="Functional Benefits" defaultOpen><PillList items={result.functional_benefits || []} color="blue" /></CollapsibleSection>
            <CollapsibleSection title="Business Benefits" defaultOpen><PillList items={result.business_benefits || []} color="emerald" /></CollapsibleSection>
            <CollapsibleSection title="Emotional Benefits" defaultOpen><PillList items={result.emotional_benefits || []} color="purple" /></CollapsibleSection>
            <div className="grid grid-cols-2 gap-3">
                <CollapsibleSection title="Why It Matters Now"><p>{result.why_it_matters_now}</p></CollapsibleSection>
                <CollapsibleSection title="Why It's Different"><p>{result.why_it_is_different}</p></CollapsibleSection>
                <CollapsibleSection title="Cost of Not Using It"><p>{result.cost_of_not_using}</p></CollapsibleSection>
                <CollapsibleSection title="Future State"><p>{result.future_state}</p></CollapsibleSection>
            </div>
            <CollapsibleSection title="Marketing Page Version" defaultOpen>
                <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: result.marketing_page_html || '' }} />
            </CollapsibleSection>
            <CollapsibleSection title="Short Version">
                <p>{result.short_version}</p>
                <button onClick={() => copyToClipboard(result.short_version)} className="mt-2 text-xs text-stone-500 hover:text-stone-700 flex items-center gap-1"><Copy className="w-3 h-3" /> Copy</button>
            </CollapsibleSection>
            <CollapsibleSection title={`Headlines (${(result.headlines || []).length})`}>
                <ol className="list-decimal list-inside space-y-1">{(result.headlines || []).map((h, i) => <li key={i} className="text-sm">{h}</li>)}</ol>
            </CollapsibleSection>
            <CollapsibleSection title={`CTAs (${(result.ctas || []).length})`}><PillList items={result.ctas || []} color="amber" /></CollapsibleSection>
            <CollapsibleSection title={`Features (${(result.features || []).length})`}>
                <div className="space-y-2">{(result.features || []).map((f, i) => (
                    <div key={i} className="border-l-2 border-stone-300 pl-3"><p className="font-medium text-sm">{f.title}</p><p className="text-xs text-stone-500">{f.description}</p></div>
                ))}</div>
            </CollapsibleSection>
        </div>
    );
}

export default function ProductAIRewriteModal({ open, onClose, baseInfoHtml, productName, productCategory, productId, getAuthHeader, onCommit, onSaveDraft, existingResult, existingPersonaResults }) {
    const [generateMode, setGenerateMode] = useState('all'); // 'all' or 'single'
    const [singlePersona, setSinglePersona] = useState('default');
    const [loading, setLoading] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState('');
    const [allResults, setAllResults] = useState(existingPersonaResults || (existingResult ? { default: existingResult } : {}));
    const [activePersonaTab, setActivePersonaTab] = useState('default');

    // Edit panel state
    const [editDescription, setEditDescription] = useState('');
    const [editContentHtml, setEditContentHtml] = useState('');
    const [editFeatures, setEditFeatures] = useState([]);

    // Current result for edit panel
    const activeResult = allResults[activePersonaTab];

    useEffect(() => {
        if (activeResult) {
            setEditDescription(activeResult.short_version || '');
            setEditContentHtml(activeResult.marketing_page_html || '');
            setEditFeatures(activeResult.features || []);
        }
    }, [activePersonaTab, activeResult]);

    useEffect(() => {
        if (open) {
            const existing = existingPersonaResults || (existingResult ? { default: existingResult } : {});
            if (Object.keys(existing).length > 0 && Object.keys(allResults).length === 0) {
                setAllResults(existing);
            }
        }
    }, [open, existingPersonaResults, existingResult]);

    const handleGenerate = async () => {
        if (!baseInfoHtml || baseInfoHtml.replace(/<[^>]*>/g, '').trim().length < 20) {
            toast.error('Please add more content in the Base tab first');
            return;
        }
        setLoading(true);
        try {
            // Auto-save unsaved products before generating
            let resolvedProductId = productId;
            if (productId === 'new' && onSaveDraft) {
                setLoadingProgress('Saving product as draft...');
                try {
                    resolvedProductId = await onSaveDraft();
                } catch (err) {
                    toast.error(err.response?.data?.detail || 'Failed to save product before generating');
                    setLoading(false);
                    setLoadingProgress('');
                    return;
                }
            }

            const isAll = generateMode === 'all';
            if (isAll) setLoadingProgress('Generating Default persona...');

            const res = await axios.post(`${API}/products/ai/rewrite`, {
                personas: isAll ? ['all'] : [singlePersona],
                base_info_html: baseInfoHtml,
                product_name: productName,
                product_category: productCategory,
                product_id: resolvedProductId !== 'new' ? resolvedProductId : undefined,
            }, { headers: getAuthHeader() });

            const results = res.data.results || {};
            // For single persona, result may be in res.data.result
            if (res.data.result && !Object.keys(results).length) {
                results[singlePersona] = res.data.result;
            }

            setAllResults(prev => ({ ...prev, ...results }));
            setActivePersonaTab(Object.keys(results)[0] || 'default');
            toast.success(isAll ? 'All 4 persona versions generated!' : 'Content generated');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'AI rewrite failed');
        } finally {
            setLoading(false);
            setLoadingProgress('');
        }
    };

    const handleCommit = () => {
        onCommit({
            description: editDescription,
            content_html: editContentHtml,
            features: editFeatures,
            ai_rewrite_result: allResults.default || allResults[activePersonaTab],
            ai_persona_results: allResults,
        });
        toast.success('AI content committed — click Save to persist');
        onClose();
    };

    const hasResults = Object.keys(allResults).length > 0;

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="flex flex-col" style={{ width: '92vw', maxWidth: '92vw', height: '90vh', maxHeight: '90vh' }}>
                <DialogHeader className="shrink-0">
                    <DialogTitle className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-amber-500" />
                        AI Product Content Rewriter
                        <span className="text-sm font-normal text-stone-400 ml-2">— {productName}</span>
                        {hasResults && (
                            <span className="ml-auto text-xs font-normal text-stone-400">{Object.keys(allResults).length}/4 personas</span>
                        )}
                    </DialogTitle>
                </DialogHeader>

                <div className="flex gap-6 flex-1 min-h-0 overflow-hidden">
                    {/* Left: AI Output */}
                    <div className="w-[55%] flex flex-col min-h-0">
                        {/* Controls */}
                        <div className="flex items-center gap-3 mb-4 shrink-0">
                            <div className="flex items-center gap-2 flex-1">
                                <select value={generateMode} onChange={e => setGenerateMode(e.target.value)}
                                    className="text-sm border border-stone-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-stone-400">
                                    <option value="all">All 4 Personas</option>
                                    <option value="single">Single Persona</option>
                                </select>
                                {generateMode === 'single' && (
                                    <select value={singlePersona} onChange={e => setSinglePersona(e.target.value)}
                                        className="text-sm border border-stone-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-stone-400 flex-1">
                                        {PERSONAS.map(p => <option key={p.value} value={p.value}>{p.icon} {p.label}</option>)}
                                    </select>
                                )}
                            </div>
                            <button onClick={handleGenerate} disabled={loading}
                                className="flex items-center gap-2 px-4 py-2 bg-stone-900 text-white text-sm font-medium rounded-lg hover:bg-stone-800 disabled:opacity-50 shrink-0">
                                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                                {loading ? 'Generating...' : hasResults ? 'Regenerate' : 'Generate'}
                            </button>
                        </div>

                        {/* Persona tabs */}
                        {hasResults && (
                            <div className="flex gap-1 mb-3 shrink-0">
                                {PERSONAS.map(p => {
                                    const hasThis = !!allResults[p.value];
                                    return (
                                        <button key={p.value}
                                            onClick={() => hasThis && setActivePersonaTab(p.value)}
                                            disabled={!hasThis}
                                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                                activePersonaTab === p.value && hasThis
                                                    ? 'bg-stone-900 text-white'
                                                    : hasThis
                                                        ? 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                                                        : 'bg-stone-50 text-stone-300 cursor-not-allowed'
                                            }`}>
                                            <span>{p.icon}</span>
                                            {p.value.charAt(0).toUpperCase() + p.value.slice(1)}
                                            {hasThis && <Check className="w-3 h-3" />}
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {/* Output */}
                        <div className="flex-1 overflow-y-auto space-y-3 pr-2">
                            {!hasResults && !loading && (
                                <div className="flex flex-col items-center justify-center h-full text-center text-stone-400 gap-3">
                                    <Sparkles className="w-12 h-12 text-stone-300" />
                                    <p className="text-sm">Select "All 4 Personas" and click Generate to create<br />structured marketing content from your base info.</p>
                                    <p className="text-xs text-stone-300">This generates 4 different messaging versions — each tailored<br />to a different audience type that AI agents will use.</p>
                                </div>
                            )}

                            {loading && (
                                <div className="flex flex-col items-center justify-center h-full text-center text-stone-400 gap-3">
                                    <Loader2 className="w-10 h-10 animate-spin text-stone-400" />
                                    <p className="text-sm">{generateMode === 'all'
                                        ? 'Generating all 4 persona versions...\nThis takes 30-60 seconds.'
                                        : 'Analyzing product information and generating\nstructured messaging framework...'}</p>
                                    {loadingProgress && <p className="text-xs text-amber-600">{loadingProgress}</p>}
                                </div>
                            )}

                            {hasResults && !loading && activeResult && (
                                <PersonaResult result={activeResult} persona={activePersonaTab} />
                            )}
                        </div>
                    </div>

                    {/* Right: Edit & Commit */}
                    <div className="w-[45%] flex flex-col min-h-0 border-l border-stone-200 pl-6">
                        <div className="flex items-center justify-between mb-4 shrink-0">
                            <h3 className="text-sm font-semibold text-stone-900 uppercase tracking-wide">Edit & Commit</h3>
                            {hasResults && (
                                <button onClick={handleCommit}
                                    className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700">
                                    <Check className="w-4 h-4" /> Commit to Product
                                </button>
                            )}
                        </div>

                        {!hasResults ? (
                            <div className="flex flex-col items-center justify-center flex-1 text-stone-400 text-center gap-2">
                                <ArrowRight className="w-8 h-8 text-stone-300 rotate-180" />
                                <p className="text-sm">Generate AI content first,<br />then edit and commit here.</p>
                            </div>
                        ) : (
                            <div className="flex-1 overflow-y-auto space-y-4 pr-1">
                                <p className="text-[10px] text-stone-400 bg-stone-50 px-3 py-2 rounded-lg">
                                    Editing from: <strong>{PERSONAS.find(p => p.value === activePersonaTab)?.label || activePersonaTab}</strong>.
                                    All persona versions are saved to the product when you commit.
                                </p>

                                {/* Short Description */}
                                <div>
                                    <label className={LABEL}>Short Description</label>
                                    <textarea value={editDescription} onChange={e => setEditDescription(e.target.value)}
                                        rows={3} className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20 resize-y" />
                                </div>

                                {/* Detailed Content */}
                                <div>
                                    <label className={LABEL}>Detailed Content</label>
                                    <RichTextEditor content={editContentHtml} onChange={setEditContentHtml} placeholder="Marketing page content..." minHeight="250px" />
                                </div>

                                {/* Features */}
                                <div>
                                    <label className={LABEL}>Features ({editFeatures.length})</label>
                                    <div className="space-y-2">
                                        {editFeatures.map((f, i) => (
                                            <div key={i} className="flex gap-2 items-start">
                                                <div className="flex-1 space-y-1">
                                                    <input value={f.title || ''} onChange={e => { const u = [...editFeatures]; u[i] = { ...u[i], title: e.target.value }; setEditFeatures(u); }}
                                                        className="w-full px-2 py-1.5 text-sm border border-stone-200 rounded-md font-medium" placeholder="Feature name" />
                                                    <input value={f.description || ''} onChange={e => { const u = [...editFeatures]; u[i] = { ...u[i], description: e.target.value }; setEditFeatures(u); }}
                                                        className="w-full px-2 py-1.5 text-xs border border-stone-200 rounded-md text-stone-600" placeholder="Description" />
                                                </div>
                                                <button onClick={() => setEditFeatures(editFeatures.filter((_, j) => j !== i))}
                                                    className="text-stone-400 hover:text-red-500 mt-1 text-xs">Remove</button>
                                            </div>
                                        ))}
                                        <button onClick={() => setEditFeatures([...editFeatures, { title: '', description: '' }])}
                                            className="text-xs text-stone-500 hover:text-stone-700">+ Add feature</button>
                                    </div>
                                </div>

                                {/* Bottom commit */}
                                <div className="pt-3 border-t border-stone-200">
                                    <button onClick={handleCommit}
                                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700">
                                        <Check className="w-4 h-4" /> Commit All Personas to Product
                                    </button>
                                    <p className="text-[10px] text-stone-400 text-center mt-2">This saves all persona versions + updates the Final Content tab. Click Save to persist.</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
